import {
	GroupResultProps,
	MultiGroupResultProps,
	MultiUserResultProps,
	ResultProps,
	MetricsProps,
	RoleType,
	UserResultProps,
} from "../../api/types";
import { api } from "api";
import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import {
	GroupResultTemplate,
	IndividualResultTemplate,
} from "../../helpers/generatePDF";
import { saveAs } from "file-saver";
import Button, { ButtonProps } from "@mui/material/Button";
import { Dialog, DialogActions, DialogTitle } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { styled } from "@mui/material/styles";
import LoadingButton from "@mui/lab/LoadingButton";
import { ErrorAlert, SuccessAlert } from "./Alert";
import JSZip from "jszip";
import { MetricsTemplate } from "../../helpers/generatePDF/MetricsPDF";
import { DateAsYYYYMMDDHHMMSS } from "../../api/base";

interface DownloadDialogProps {
	role: RoleType;
	loading: boolean;
	baseName?: string;
	resultToBeRendered: ResultProps;
	templateType: "individual" | "group" | "metrics";
	// these two params are used when users want to
	// multi-check and download results on TablePage
	checkOpen?: boolean;
	onCheckOpen?: (open: boolean) => void;
}

let normalColorCode: string, hoverColorCode: string;
let showDownloadDialogButton: boolean;

export const DownloadDialog: React.FC<DownloadDialogProps> = ({
	role,
	loading,
	baseName = "result",
	resultToBeRendered,
	templateType,
	checkOpen,
	onCheckOpen,
}): JSX.Element => {
	const [open, setOpen] = useState(false);
	const [success, setSuccess] = useState(false);
	const [error, setError] = useState("");
	const [email, setEmail] = useState("");

	normalColorCode = role === "student" ? "#025ddc" : "#09203F";
	hoverColorCode = role === "student" ? "#0247a8" : "#042552";
	showDownloadDialogButton = checkOpen === undefined;

	const handleClickOpen = (): void => {
		onCheckOpen && onCheckOpen(true);
		setOpen(true);
	};

	const handleClose = (): void => {
		onCheckOpen && onCheckOpen(false);
		setOpen(false);
	};

	const handleClickEmail = (): void => {
		onCheckOpen && onCheckOpen(false);
		setOpen(false);
		resultDispatcher()
			.then(([blob, fileName]) => {
				api
					// get my user data
					.getMyUserData()
					.then((res) => {
						if (res.status !== 200) {
							throw new Error();
						}
						return res.data;
					})
					.then((userData) => {
						// construct a multipart/form-data body
						const result: FormData = new FormData();
						result.append("file", blob, fileName);
						result.append("emailTo", userData.email);
						result.append(
							"userName",
							userData.firstName + " " + userData.lastName,
						);

						setEmail(userData.email);
						setSuccess(true);
						// post data to backend
						api
							.sendResult(result)
							.then((res) => {
								if (res.status === 201) {
									return;
								}
								if (res.status === 400) {
									setError("Invalid PDF file");
									return;
								}
							})
							.catch((err) => {
								setError(err.message);
							});
					})
					.catch((err) => {
						setError(err.message);
					});
			})
			.catch((err) => {
				setError(err.message);
			});
	};

	const handleClickDownload = (): void => {
		setOpen(false);
		resultDispatcher()
			.then(([blob, fileName]) => {
				saveAs(blob, fileName);
			})
			.catch((err) => {
				setError(err.message);
			});
	};

	const resultDispatcher = async (): Promise<[Blob, string]> => {
		let blob: Blob = new Blob();
		let fileName: string = "";
		// called from TablePage
		if (onCheckOpen) {
			onCheckOpen(false);
			const zip = new JSZip();
			const results =
				templateType === "individual"
					? (resultToBeRendered as MultiUserResultProps)
					: (resultToBeRendered as MultiGroupResultProps);
			for (const resProps of results.propsList) {
				const pdfFile: Blob = await pdf(
					templateType === "individual" ? (
						<IndividualResultTemplate data={resProps as UserResultProps} />
					) : (
						<GroupResultTemplate data={resProps as GroupResultProps} />
					),
				).toBlob();
				// ID is used to avoid same student/group names, in which case prior files will be overwritten
				const id: string =
					resProps.id === undefined ? "" : resProps.id.substring(0, 8);
				zip.file(resProps.name + "'s Academic Result-" + id + ".pdf", pdfFile);
			}
			await zip
				.generateAsync({ type: "blob" })
				.then(function (content) {
					blob = content;
					fileName = "Result-" + DateAsYYYYMMDDHHMMSS(new Date()) + ".zip";
					return [blob, fileName];
				})
				.catch(function (err) {
					setError(err.message);
				});
		}
		// called from result/metrics details page
		else {
			if (templateType === "individual") {
				blob = await pdf(
					<IndividualResultTemplate
						data={resultToBeRendered as UserResultProps}
					/>,
				).toBlob();
			} else if (templateType === "group") {
				blob = await pdf(
					<GroupResultTemplate data={resultToBeRendered as GroupResultProps} />,
				).toBlob();
			} else {
				blob = await pdf(
					<MetricsTemplate data={resultToBeRendered as MetricsProps} />,
				).toBlob();
			}
			fileName = baseName;
		}
		return [blob, fileName];
	};

	return (
		<div>
			<div>
				{showDownloadDialogButton && (
					<DownloadDialogButton
						variant="contained"
						onClick={handleClickOpen}
						loading={loading}
						loadingIndicator="Loading..."
					>
						DOWNLOAD
					</DownloadDialogButton>
				)}
				<Dialog
					open={checkOpen === undefined ? open : checkOpen}
					onClose={handleClose}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
				>
					<DialogTitle id="alert-dialog-title">
						{"Confirm to start downloading the result"}
					</DialogTitle>
					<DialogActions>
						<Button onClick={handleClose}>Cancel</Button>
						<ColoredButton
							size="small"
							variant="contained"
							onClick={handleClickDownload}
							endIcon={<SaveIcon />}
							autoFocus
						>
							Save
						</ColoredButton>

						<ColoredButton
							size="small"
							variant="contained"
							onClick={handleClickEmail}
							endIcon={<SendIcon />}
						>
							Email me
						</ColoredButton>
					</DialogActions>
				</Dialog>
			</div>
			<div>{error && <ErrorAlert error={error} />}</div>
			<div>{success && <SuccessAlert email={email} />}</div>
		</div>
	);
};

const DownloadDialogButton = styled(LoadingButton)<ButtonProps>(
	({ theme }) => ({
		color: theme.palette.getContrastText(normalColorCode),
		backgroundColor: normalColorCode,
		"&:hover": {
			backgroundColor: hoverColorCode,
		},
	}),
);

const ColoredButton = styled(Button)<ButtonProps>(({ theme }) => ({
	color: theme.palette.getContrastText(normalColorCode),
	backgroundColor: normalColorCode,
	"&:hover": {
		backgroundColor: hoverColorCode,
	},
}));
